import MonthlyChampionshipAdmin from "./MonthlyChampionshipAdmin";
import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Layers, ListChecks, Swords, Upload, Trophy, Settings as SettingsIcon, Plus, Trash2, RefreshCw, Filter, Play, Pause, Save, Download, ChevronRight, CircleDot, Shield, FileText, Award } from "lucide-react";

const POOL_META = {
  practice: { label: "Practice", cls: "bg-[#2C4C3B]/10 text-[#2C4C3B] border-[#2C4C3B]/30" },
  challenge: { label: "Challenge", cls: "bg-purple-50 text-purple-700 border-purple-200" },
};
const CHAL_STATUS_META = {
  draft: { label: "Draft", cls: "bg-[#F3EFEA] text-[#5C6661]" },
  scheduled: { label: "Scheduled", cls: "bg-blue-50 text-blue-700" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
  closed: { label: "Closed", cls: "bg-orange-100 text-orange-800" },
  results_pending: { label: "Results Pending", cls: "bg-amber-100 text-amber-800" },
  results_published: { label: "Results Published", cls: "bg-[#2C4C3B]/10 text-[#2C4C3B]" },
  archived: { label: "Archived", cls: "bg-neutral-200 text-neutral-700" },
};
const DIFFICULTIES = ["Easy", "Moderate", "Difficult", "Very Difficult"];

function StatusPill({ meta, children }) {
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}>{children || meta.label}</span>;
}

// ============ Categories ============
function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = async () => setItems((await api.get("/quiz/categories")).data || []);
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/quiz/admin/categories", { name: name.trim(), active: true }); setName(""); await load(); toast.success("Category saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Deactivate this category?")) return; await api.delete(`/quiz/admin/categories/${id}`); await load(); };
  return (
    <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl" data-testid="quiz-categories-panel">
      <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1 flex items-center gap-2"><Layers className="w-5 h-5 text-[#2C4C3B]" /> Categories</h3>
      <p className="text-xs text-[#5C6661] mb-4">Categories apply to both Practice and Challenge banks.</p>
      <div className="flex gap-2 mb-4">
        <input data-testid="cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" className="flex-1 px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        <button data-testid="cat-add" onClick={add} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="divide-y divide-[#E5E0D8]">
        {items.map((c, i) => (
          <div key={c.id} data-testid={`cat-row-${i}`} className="py-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-[#1C201E]">{c.name}</div>
              <div className="text-xs text-[#5C6661]">{c.slug}{!c.active ? " · inactive" : ""}</div>
            </div>
            <button onClick={() => del(c.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[#5C6661]">No categories yet.</div>}
      </div>
    </div>
  );
}

// ============ Question Form ============
function QuestionForm({ pool, categories, initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { category: categories[0]?.name || "General", question: "", choice_a: "", choice_b: "", choice_c: "", choice_d: "", correct_answer: "A", bible_reference: "", explanation: "", difficulty: "Moderate", active: true });
  const save = async () => {
    try {
      await onSave({ ...f, question_pool: pool });
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-5 bg-[#F3EFEA]/60 border border-[#D4A373]/40 rounded-xl space-y-3" data-testid="q-form">
      <div className="grid md:grid-cols-3 gap-3">
        <select data-testid="q-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white">
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select data-testid="q-difficulty" value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white">
          {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-[#1C201E]"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
      </div>
      <textarea data-testid="q-question" rows={2} placeholder="Question text" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
      <div className="grid md:grid-cols-2 gap-2">
        {["a", "b", "c", "d"].map((k) => (
          <div key={k} className="flex gap-2 items-center">
            <label className="w-6 text-sm font-semibold text-[#5C6661]">{k.toUpperCase()}</label>
            <input data-testid={`q-choice-${k}`} value={f[`choice_${k}`]} onChange={(e) => setF({ ...f, [`choice_${k}`]: e.target.value })} placeholder={`Choice ${k.toUpperCase()}`} className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8]" />
            <input type="radio" name="correct" data-testid={`q-correct-${k}`} checked={f.correct_answer === k.toUpperCase()} onChange={() => setF({ ...f, correct_answer: k.toUpperCase() })} title="Mark as correct" />
          </div>
        ))}
      </div>
      <input data-testid="q-reference" placeholder="Bible reference (e.g. Genesis 6:14)" value={f.bible_reference} onChange={(e) => setF({ ...f, bible_reference: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8]" />
      <textarea data-testid="q-explanation" rows={2} placeholder="Optional explanation" value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8]" />
      <div className="flex justify-end gap-2">
        {onCancel && <button onClick={onCancel} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Cancel</button>}
        <button data-testid="q-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
}

// ============ Question Bank (per pool) ============
function QuestionBankPanel({ pool }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ category_id: "", active: "all", used: "all", q: "" });
  const load = async () => {
    const params = { pool };
    if (filter.category_id) params.category_id = filter.category_id;
    if (filter.active !== "all") params.active = filter.active === "true";
    if (filter.used !== "all") params.used = filter.used === "true";
    if (filter.q.trim()) params.q = filter.q.trim();
    const qs = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    setItems((await api.get(`/quiz/admin/questions?${qs}`)).data || []);
  };
  const loadCats = async () => setCats((await api.get("/quiz/categories")).data || []);
  useEffect(() => { loadCats(); }, []);
  useEffect(() => { load(); }, [filter, pool]); // eslint-disable-line

  const save = async (payload) => {
    if (editing) await api.put(`/quiz/admin/questions/${editing.id}`, payload);
    else await api.post("/quiz/admin/questions", payload);
    setEditing(null); setShowForm(false); await load();
  };
  const del = async (id) => { if (!window.confirm("Delete this question?")) return; await api.delete(`/quiz/admin/questions/${id}`); await load(); };
  const openEdit = (q) => {
    const cA = q.choices.find((c) => c.key === "A")?.text || "";
    const cB = q.choices.find((c) => c.key === "B")?.text || "";
    const cC = q.choices.find((c) => c.key === "C")?.text || "";
    const cD = q.choices.find((c) => c.key === "D")?.text || "";
    setEditing({ ...q, choice_a: cA, choice_b: cB, choice_c: cC, choice_d: cD }); setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid={`qbank-${pool}`}>
      <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-[#5C6661]" />
          <select data-testid="qb-filter-category" value={filter.category_id} onChange={(e) => setFilter({ ...filter, category_id: e.target.value })} className="px-3 py-1.5 rounded-lg border border-[#E5E0D8] text-sm">
            <option value="">All categories</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="qb-filter-active" value={filter.active} onChange={(e) => setFilter({ ...filter, active: e.target.value })} className="px-3 py-1.5 rounded-lg border border-[#E5E0D8] text-sm">
            <option value="all">All</option><option value="true">Active</option><option value="false">Inactive</option>
          </select>
          {pool === "challenge" && (
            <select data-testid="qb-filter-used" value={filter.used} onChange={(e) => setFilter({ ...filter, used: e.target.value })} className="px-3 py-1.5 rounded-lg border border-[#E5E0D8] text-sm">
              <option value="all">Any usage</option><option value="false">Unused</option><option value="true">Used</option>
            </select>
          )}
          <input data-testid="qb-filter-q" placeholder="Search…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="px-3 py-1.5 rounded-lg border border-[#E5E0D8] text-sm" />
        </div>
        <button data-testid="qb-new" onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Plus className="w-4 h-4" /> New question</button>
      </div>

      {showForm && (
        <QuestionForm pool={pool} categories={cats} initial={editing} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} />
      )}

      <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl">
        <div className="text-sm text-[#5C6661] mb-3">{items.length} questions in the {POOL_META[pool].label} bank</div>
        <div className="divide-y divide-[#E5E0D8]">
          {items.map((q, i) => (
            <div key={q.id} data-testid={`qb-row-${i}`} className="py-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill meta={POOL_META[pool]} />
                  <span className="text-xs uppercase tracking-widest text-[#5C6661]">{q.category}</span>
                  <span className="text-xs text-[#5C6661]">· {q.difficulty}</span>
                  {!q.active && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700">inactive</span>}
                  {q.used && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">used</span>}
                  {q.transferred_to_practice && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">transferred</span>}
                </div>
                <div className="font-medium text-[#1C201E] mt-1">{q.question}</div>
                <div className="text-xs text-[#5C6661] mt-0.5">Answer: <b>{q.correct_answer}</b> · {q.bible_reference}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(q)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-xs hover:bg-[#F3EFEA]">Edit</button>
                <button onClick={() => del(q.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661]">No questions found.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ Weekly Challenges ============
function WeeklyChallengesPanel() {
  const [items, setItems] = useState([]);
  const [chQuestions, setChQuestions] = useState([]);
  const [recycle, setRecycle] = useState(false);
  const [questionSearch, setQuestionSearch] = useState("");
  const [questionCategory, setQuestionCategory] = useState("");
  const [questionDifficulty, setQuestionDifficulty] = useState("");
  const [questionPage, setQuestionPage] = useState(0);
  const [questionCategories, setQuestionCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [form, setForm] = useState({ title: "", theme: "", description: "", banner_url: "", open_date: "", close_date: "", publish_date: "", question_ids: [] });
  const load = async () => setItems((await api.get("/quiz/admin/weekly-challenges")).data || []);
  useEffect(() => { load(); api.get("/quiz/categories").then(({ data }) => setQuestionCategories(data || [])).catch(() => toast.error("Could not load question categories")); }, []);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/quiz/admin/questions", { params: { pool: "challenge", active: true, challenge_id: editing?.id, ...(recycle ? {} : { used: false }), q: questionSearch, category_id: questionCategory || undefined, difficulty: questionDifficulty || undefined, limit: 100, offset: questionPage * 100 } });
        if (active) setChQuestions(data || []);
      } catch { if (active) toast.error("Could not load challenge questions"); }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [recycle, questionSearch, questionCategory, questionDifficulty, questionPage, showForm, editing?.id]);
  const openNew = () => { setRecycle(false); setQuestionPage(0); setEditing(null); setForm({ title: "", theme: "", description: "", banner_url: "", open_date: "", close_date: "", publish_date: "", question_ids: [] }); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ title: c.title, theme: c.theme || "", description: c.description || "", banner_url: c.banner_url || "", open_date: c.open_date, close_date: c.close_date, publish_date: c.publish_date, question_ids: c.question_ids || [] });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.title.trim() || !form.open_date || !form.close_date || !form.publish_date) { toast.error("Title and dates required"); return; }
    try {
      const payload = { ...form, include_previously_used: recycle };
      if (editing) await api.put(`/quiz/admin/weekly-challenges/${editing.id}`, payload);
      else await api.post("/quiz/admin/weekly-challenges", payload);
      setShowForm(false); await load(); toast.success("Challenge saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const schedule = async (id) => { try { await api.post(`/quiz/admin/weekly-challenges/${id}/schedule`); await load(); toast.success("Scheduled"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const publish = async (id) => { try { await api.post(`/quiz/admin/weekly-challenges/${id}/publish-results`); await load(); toast.success("Results published + transfer complete"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const toggleHold = async (id) => { try { await api.post(`/quiz/admin/weekly-challenges/${id}/hold`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const doPreview = async (c) => { try { const { data } = await api.get(`/quiz/admin/weekly-challenges/${c.id}/preview`); setPreview(data); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const seeAttempts = async (c) => { try { const { data } = await api.get(`/quiz/admin/weekly-challenges/${c.id}/attempts`); setAttempts({ challenge: c, list: data }); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this draft/scheduled challenge?")) return; try { await api.delete(`/quiz/admin/weekly-challenges/${id}`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const retry = async (id) => { try { await api.post(`/quiz/admin/weekly-challenges/${id}/retry-transfer`); toast.success("Transfer retried"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const toggleQ = (qid) => {
    setForm((f) => {
      const has = f.question_ids.includes(qid);
      if (has) return { ...f, question_ids: f.question_ids.filter((x) => x !== qid) };
      if (f.question_ids.length >= 10) { toast.error("Exactly 10 questions per challenge"); return f; }
      return { ...f, question_ids: [...f.question_ids, qid] };
    });
  };

  return (
    <div className="space-y-4" data-testid="weekly-challenges-panel">
      <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><Swords className="w-5 h-5 text-[#2C4C3B]" /><span className="font-serif-display text-xl text-[#1C201E]">Weekly Bible Challenges</span></div>
        <button data-testid="wc-new" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Plus className="w-4 h-4" /> New challenge</button>
      </div>

      {showForm && (
        <div className="p-5 bg-[#F3EFEA]/60 border border-[#D4A373]/40 rounded-xl space-y-3" data-testid="wc-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="wc-title" placeholder="Title (e.g. Week 36 — Life of David)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8]" />
            <input data-testid="wc-theme" placeholder="Theme (optional)" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8]" />
          </div>
          <textarea rows={2} data-testid="wc-desc" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8]" />
          <input data-testid="wc-banner" placeholder="Banner URL (optional)" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8]" />
          <div className="grid md:grid-cols-3 gap-3">
            <label className="text-sm text-[#5C6661]">Open (Mon)<input data-testid="wc-open" type="date" value={form.open_date} onChange={(e) => setForm({ ...form, open_date: e.target.value })} className="w-full mt-1 px-4 py-2 rounded-lg border border-[#E5E0D8]" /></label>
            <label className="text-sm text-[#5C6661]">Close (Sat)<input data-testid="wc-close" type="date" value={form.close_date} onChange={(e) => setForm({ ...form, close_date: e.target.value })} className="w-full mt-1 px-4 py-2 rounded-lg border border-[#E5E0D8]" /></label>
            <label className="text-sm text-[#5C6661]">Publish (Sun)<input data-testid="wc-publish" type="date" value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} className="w-full mt-1 px-4 py-2 rounded-lg border border-[#E5E0D8]" /></label>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#5C6661] mb-2">Select exactly 10 challenge questions ({form.question_ids.length}/10)</div>
            <label className="flex items-center gap-2 mb-2 text-sm"><input type="checkbox" checked={recycle} onChange={e => { setRecycle(e.target.checked); setQuestionPage(0); }}/>Include previously used questions</label>
            <div className="flex flex-wrap gap-2 mb-2"><input aria-label="Search challenge questions" placeholder="Search text, topic, reference" value={questionSearch} onChange={e => { setQuestionSearch(e.target.value); setQuestionPage(0); }} className="rounded-lg border px-3 py-2"/>
            <select aria-label="Question category" value={questionCategory} onChange={e => { setQuestionCategory(e.target.value); setQuestionPage(0); }}><option value="">All categories</option>{questionCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            <select aria-label="Question difficulty" value={questionDifficulty} onChange={e => { setQuestionDifficulty(e.target.value); setQuestionPage(0); }}><option value="">All difficulties</option>{["Easy","Moderate","Difficult","Very Difficult"].map(d => <option key={d}>{d}</option>)}</select></div>
            <p className="text-xs mb-2">{form.question_ids.length} selected questions are kept when you search or change filters.</p>
            <div className="max-h-64 overflow-auto border border-[#E5E0D8] rounded-lg p-2">
              {chQuestions.map((q, i) => {
                const checked = form.question_ids.includes(q.id);
                return (
                  <label key={q.id} data-testid={`wc-q-pick-${i}`} className={`flex items-start gap-2 py-1.5 px-2 rounded ${checked ? "bg-[#2C4C3B]/10" : "hover:bg-[#F3EFEA]"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleQ(q.id)} disabled={q.used && !recycle && !checked} className="mt-1" />
                    <div className="text-sm">
                      <div className="text-[#1C201E]">{q.question}</div>
                      <div className="text-xs text-[#5C6661]">{q.category} · {q.difficulty} · {q.bible_reference}{q.used && ` · Previously Used · ${q.usage_count || 1} use(s) · ${q.last_challenge_used || "Previous challenge"} · ${q.last_date_used || ""}`}</div>
                    </div>
                  </label>
                );
              })}
              {chQuestions.length === 0 && <div className="text-xs text-[#5C6661] p-2">No questions match these filters.</div>}
            </div>
          </div>
          <div className="flex gap-3"><button disabled={questionPage === 0} onClick={() => setQuestionPage(p => p - 1)}>Previous questions</button><span>Page {questionPage + 1}</span><button disabled={chQuestions.length < 100} onClick={() => setQuestionPage(p => p + 1)}>Next questions</button></div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Cancel</button>
            <button data-testid="wc-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Save className="w-4 h-4" /> Save</button>
          </div>
        </div>
      )}

      <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl divide-y divide-[#E5E0D8]">
        {items.map((c, i) => (
          <div key={c.id} data-testid={`wc-row-${i}`} className="py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill meta={CHAL_STATUS_META[c.status] || CHAL_STATUS_META.draft} />
                <span className="text-xs uppercase tracking-widest text-[#5C6661]">{c.open_date} → {c.close_date} · pub {c.publish_date}</span>
                {c.hold_publish && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700">on hold</span>}
              </div>
              <div className="font-serif-display text-lg text-[#1C201E] mt-1">{c.title}</div>
              {c.theme && <div className="text-sm text-[#5C6661]">{c.theme}</div>}
              <div className="text-xs text-[#5C6661] mt-1">{c.question_ids?.length || 0}/10 questions · {c.participant_count} participants</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => doPreview(c)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-xs">Preview</button>
              {(c.status === "draft" || c.status === "scheduled") && <button onClick={() => openEdit(c)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-xs">Edit</button>}
              {c.status === "draft" && <button data-testid={`wc-schedule-${i}`} onClick={() => schedule(c.id)} className="px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs">Schedule</button>}
              {(c.status === "closed" || c.status === "results_pending") && <button data-testid={`wc-publish-${i}`} onClick={() => publish(c.id)} className="px-3 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs inline-flex items-center gap-1"><Trophy className="w-3 h-3" /> Publish results</button>}
              {(c.status === "closed" || c.status === "results_pending" || c.status === "results_published") && <button onClick={() => toggleHold(c.id)} className="px-3 py-1.5 rounded-full border border-orange-400 text-orange-800 text-xs inline-flex items-center gap-1">{c.hold_publish ? <><Play className="w-3 h-3" /> Release</> : <><Pause className="w-3 h-3" /> Hold</>}</button>}
              {c.status === "results_published" && <button onClick={() => retry(c.id)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-xs inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Retry transfer</button>}
              <button onClick={() => seeAttempts(c)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-xs">Attempts</button>
              {(c.status === "draft" || c.status === "scheduled") && <button onClick={() => del(c.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[#5C6661]">No challenges yet.</div>}
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-auto rounded-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-serif-display text-xl text-[#1C201E] mb-2">Preview — {preview.title}</h4>
            <div className="text-xs text-[#5C6661] mb-4">{preview.status} · {preview.open_date} → {preview.close_date}</div>
            <ol className="space-y-3 list-decimal list-inside">
              {(preview.snapshot || []).map((q, i) => (
                <li key={i} className="p-3 border border-[#E5E0D8] rounded-lg">
                  <div className="font-medium">{q.question}</div>
                  <ul className="text-sm mt-2 space-y-0.5">
                    {q.choices.map((c) => <li key={c.key} className={c.key === q.correct_answer_key ? "text-[#2C4C3B] font-medium" : "text-[#5C6661]"}>{c.key}. {c.text} {c.key === q.correct_answer_key && "✓"}</li>)}
                  </ul>
                  <div className="text-xs text-[#5C6661] mt-1">{q.bible_reference} · {q.difficulty}</div>
                </li>
              ))}
            </ol>
            <div className="text-right mt-4"><button onClick={() => setPreview(null)} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Close</button></div>
          </div>
        </div>
      )}

      {attempts && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAttempts(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-auto rounded-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-serif-display text-xl text-[#1C201E] mb-2">Attempts — {attempts.challenge.title}</h4>
            <div className="text-xs text-[#5C6661] mb-4">{attempts.list.length} completed</div>
            <div className="divide-y divide-[#E5E0D8]">
              {attempts.list.map((a, i) => (
                <div key={i} className="py-2 flex items-center justify-between">
                  <div><div className="font-medium text-[#1C201E]">#{a.rank} {a.user_name}</div><div className="text-xs text-[#5C6661]">Answered: {a.answered_count}/{a.total_questions} · Correct: {a.correct_count}/{a.total_questions} · Incorrect: {a.incorrect_count} · {a.total_completion_ms}ms</div></div>
                  <div className="font-serif-display text-lg text-[#2C4C3B]">{a.total_score}<span className="text-xs text-[#5C6661]"> / 200</span></div>
                </div>
              ))}
              {attempts.list.length === 0 && <div className="text-sm text-[#5C6661]">No attempts yet.</div>}
            </div>
            <div className="text-right mt-4"><button onClick={() => setAttempts(null)} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CSV Import ============
function CsvImportPanel() {
  const [pool, setPool] = useState("practice");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [skipDupes, setSkipDupes] = useState(true);
  const loadHistory = async () => setHistory((await api.get("/quiz/admin/imports")).data || []);
  useEffect(() => { loadHistory(); }, []);

  const doDownload = async (kind) => {
    try {
      const { data } = await api.get(`/quiz/admin/templates/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bbc-connect-${kind}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download template");
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("forced_pool", pool);
    try {
      const { data } = await api.post("/quiz/admin/csv/preview", fd);
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const confirm = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const { data } = await api.post("/quiz/admin/csv/confirm", { filename: preview.filename, question_pool: pool, rows: preview.rows, skip_duplicates: skipDupes });
      toast.success(`Imported ${data.summary.imported}, skipped ${data.summary.skipped}`);
      setPreview(null); setFile(null); await loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const validRows = preview ? preview.rows.filter((r) => r.errors.length === 0) : [];
  return (
    <div className="space-y-6" data-testid="csv-import-panel">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1 flex items-center gap-2"><Upload className="w-5 h-5 text-[#2C4C3B]" /> CSV Batch Upload</h3>
        <p className="text-xs text-[#5C6661] mb-4">Max {500} rows per file. UTF-8 with proper quoting.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          <button data-testid="csv-tpl-practice" onClick={() => doDownload("practice")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm hover:bg-[#2C4C3B] hover:text-white"><Download className="w-4 h-4" /> Practice template</button>
          <button data-testid="csv-tpl-challenge" onClick={() => doDownload("challenge")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm hover:bg-[#2C4C3B] hover:text-white"><Download className="w-4 h-4" /> Challenge template</button>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <select data-testid="csv-pool" value={pool} onChange={(e) => setPool(e.target.value)} className="px-4 py-2 rounded-lg border border-[#E5E0D8]">
            <option value="practice">Practice import</option>
            <option value="challenge">Challenge import</option>
          </select>
          <input data-testid="csv-file" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          <button data-testid="csv-upload" onClick={upload} disabled={!file} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60"><Upload className="w-4 h-4" /> Parse &amp; preview</button>
        </div>
      </div>

      {preview && (
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
          <div className="grid grid-cols-4 gap-3 mb-4 text-center">
            <div className="p-3 bg-[#F3EFEA] rounded-lg"><div className="text-2xl font-serif-display text-[#1C201E]">{preview.total}</div><div className="text-xs uppercase tracking-widest text-[#5C6661]">Total</div></div>
            <div className="p-3 bg-emerald-50 rounded-lg"><div className="text-2xl font-serif-display text-emerald-800">{preview.valid}</div><div className="text-xs uppercase tracking-widest text-emerald-700">Valid</div></div>
            <div className="p-3 bg-amber-50 rounded-lg"><div className="text-2xl font-serif-display text-amber-800">{preview.warnings}</div><div className="text-xs uppercase tracking-widest text-amber-700">Warnings</div></div>
            <div className="p-3 bg-red-50 rounded-lg"><div className="text-2xl font-serif-display text-red-800">{preview.errors}</div><div className="text-xs uppercase tracking-widest text-red-700">Errors</div></div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#1C201E] mb-3"><input type="checkbox" checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} /> Skip duplicates ({preview.duplicates} detected)</label>
          <div className="max-h-96 overflow-auto border border-[#E5E0D8] rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-[#F3EFEA] sticky top-0"><tr>
                <th className="p-2 text-left">Row</th><th className="p-2 text-left">Pool</th><th className="p-2 text-left">Category</th><th className="p-2 text-left">Question</th><th className="p-2 text-left">Ans</th><th className="p-2 text-left">Reference</th><th className="p-2 text-left">Status</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className={r.errors.length ? "bg-red-50" : r.duplicate ? "bg-amber-50" : ""}>
                    <td className="p-2">{r.row}</td>
                    <td className="p-2">{r.question_pool}</td>
                    <td className="p-2">{r.category}</td>
                    <td className="p-2 max-w-[240px] truncate" title={r.question}>{r.question}</td>
                    <td className="p-2">{r.correct_answer}</td>
                    <td className="p-2">{r.bible_reference}</td>
                    <td className="p-2 text-red-700">{r.errors.join("; ") || (r.duplicate ? "duplicate" : r.warnings.join("; ") || "ok")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => { setPreview(null); setFile(null); }} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Cancel</button>
            <button data-testid="csv-confirm" onClick={confirm} disabled={importing || validRows.length === 0} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">{importing ? "Importing…" : `Import ${validRows.length - (skipDupes ? preview.duplicates : 0)} rows`}</button>
          </div>
        </div>
      )}

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h4 className="font-serif-display text-xl text-[#1C201E] mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#8a5a2a]" /> Import history</h4>
        <div className="divide-y divide-[#E5E0D8]">
          {history.map((h, i) => (
            <div key={i} className="py-2 flex items-center justify-between text-sm">
              <div><div className="text-[#1C201E]">{h.filename} · {h.question_pool || "combined"}</div><div className="text-xs text-[#5C6661]">{new Date(h.imported_at).toLocaleString()} · by {h.imported_by}</div></div>
              <div className="text-xs text-[#5C6661]">imported {h.imported} · skipped {h.skipped}</div>
            </div>
          ))}
          {history.length === 0 && <div className="text-sm text-[#5C6661]">No imports yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ Champions ============
function ChampionsPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [annual, setAnnual] = useState(null);
  const [hall, setHall] = useState([]);
  const load = async () => {
    setAnnual((await api.get(`/quiz/championship/annual?year=${year}`)).data);
    setHall((await api.get("/quiz/championship/hall")).data || []);
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line
  const finalize = async () => {
    if (!window.confirm(`Finalize year ${year} into Hall of Champions? This cannot be undone.`)) return;
    try { await api.post(`/quiz/admin/championship/finalize?year=${year}`); toast.success("Year finalized"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="champions-panel">
      <MonthlyChampionshipAdmin />
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2"><Award className="w-5 h-5 text-[#D4A373]" /> BBC Scripture Championship — {year}</h3>
          <div className="flex items-center gap-2">
            <input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(parseInt(e.target.value || 0, 10))} className="w-24 px-3 py-1.5 rounded-lg border border-[#E5E0D8] text-sm" />
            <button onClick={finalize} className="px-4 py-1.5 rounded-full border border-[#D4A373] text-[#8a5a2a] text-xs hover:bg-[#D4A373]/20">Finalize {year}</button>
          </div>
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {(annual?.rankings || []).map((r) => (
            <div key={r.user_id} className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#2C4C3B] text-white text-sm flex items-center justify-center font-serif-display">{r.rank}</div>
                <div><div className="font-medium text-[#1C201E]">{r.user_name}</div><div className="text-xs text-[#5C6661]">{r.correct_answers} correct · {r.challenges_played} completed weeks</div></div>
              </div>
              <div className="font-serif-display text-xl text-[#2C4C3B]">{Number(r.percentage ?? r.total_score).toFixed(2)}%</div>
            </div>
          ))}
          {(!annual?.rankings || annual.rankings.length === 0) && <div className="text-sm text-[#5C6661]">No data yet for {year}.</div>}
        </div>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h4 className="font-serif-display text-xl text-[#1C201E] mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#D4A373]" /> Hall of Champions</h4>
        <div className="grid md:grid-cols-2 gap-3">
          {hall.map((c) => (
            <div key={c.year} className="p-4 border border-[#D4A373]/40 rounded-xl bg-gradient-to-br from-[#F3EFEA] to-white">
              <div className="text-xs uppercase tracking-widest text-[#8a5a2a]">Champion {c.year}</div>
              <div className="font-serif-display text-lg text-[#1C201E] mt-1">{c.name}</div>
              <div className="text-xs text-[#5C6661]">{c.total_score} pts · {c.weekly_wins} weekly wins · {c.challenges_played} played</div>
            </div>
          ))}
          {hall.length === 0 && <div className="text-sm text-[#5C6661]">No champions recorded yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ Settings ============
function SettingsPanel({ isSuper }) {
  const [s, setS] = useState(null);
  const load = async () => setS((await api.get("/quiz/admin/settings")).data);
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.put("/quiz/admin/settings", s); toast.success("Settings saved"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  if (!s) return null;
  return (
    <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl max-w-xl" data-testid="quiz-settings">
      <h3 className="font-serif-display text-2xl text-[#1C201E] mb-4 flex items-center gap-2"><SettingsIcon className="w-5 h-5 text-[#2C4C3B]" /> Quiz Settings</h3>
      <div className="space-y-3 text-sm text-[#1C201E]">
        <label className="flex items-center gap-2"><input type="checkbox" checked={s.auto_transfer_to_practice} onChange={(e) => setS({ ...s, auto_transfer_to_practice: e.target.checked })} disabled={!isSuper} /> Automatically add completed Weekly Challenge questions to Bible Practice</label>
        <label className="block">Transfer destination category<input value={s.transfer_category} onChange={(e) => setS({ ...s, transfer_category: e.target.value })} disabled={!isSuper} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E0D8]" /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={s.enable_saturday_reminder} onChange={(e) => setS({ ...s, enable_saturday_reminder: e.target.checked })} disabled={!isSuper} /> Send Saturday reminder to eligible users who have not attempted</label>
      </div>
      <button onClick={save} disabled={!isSuper} className="mt-4 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60 inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
      {!isSuper && <div className="text-xs text-[#5C6661] mt-2">Only super admins can change these settings.</div>}
    </div>
  );
}

function RankingsPanel({ period = "weekly" }) {
  const manilaDate = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const [year, setYear] = useState(Number(manilaDate.slice(0,4))), [month, setMonth] = useState(Number(manilaDate.slice(5,7)));
  const [challenges, setChallenges] = useState([]), [challengeId, setChallengeId] = useState(""), [rankings, setRankings] = useState([]), [error, setError] = useState("");
  useEffect(() => { if (period !== "weekly") return; let active = true; api.get("/quiz/admin/weekly-challenges").then(({ data }) => { if (active) { setChallenges(data || []); setChallengeId((data || []).find(c => c.status === "results_published")?.id || ""); } }).catch(() => setError("Challenges could not be loaded")); return () => { active = false; }; }, [period]);
  useEffect(() => {
    let active = true; setError(""); setRankings([]);
    if (period === "weekly" && !challengeId) return;
    const endpoint = period === "weekly" ? `/quiz/admin/weekly-challenges/${challengeId}/attempts` : `/quiz/championship/monthly?year=${year}&month=${month}`;
    api.get(endpoint).then(({ data }) => { if (active) setRankings(Array.isArray(data) ? data : data.rankings || []); }).catch(() => { if (active) setError("Rankings could not be loaded. Retry while online."); });
    return () => { active = false; };
  }, [period, challengeId, year, month]);
  const challenge = challenges.find(c => c.id === challengeId);
  return <section className="rounded-xl border bg-white p-5"><h3 className="font-serif-display text-2xl">{period === "weekly" ? "Weekly Rankings & Winners" : "Monthly Rankings & Winners"}</h3><p className="my-2 text-sm text-[#5C6661]">Asia/Manila · Rankings calculated by BBC Connect API</p>
    {period === "weekly" ? <select aria-label="Leaderboard challenge" value={challengeId} onChange={e => setChallengeId(e.target.value)} className="input"><option value="">Select challenge</option>{challenges.map(c => <option key={c.id} value={c.id}>{c.title} · {c.status}</option>)}</select> : <div className="flex gap-3"><input aria-label="Ranking year" type="number" min="2000" max="2100" value={year} onChange={e => setYear(Number(e.target.value))}/><select aria-label="Ranking month" value={month} onChange={e => setMonth(Number(e.target.value))}>{Array.from({length:12},(_,i) => <option key={i+1} value={i+1}>{i+1}</option>)}</select></div>}
    {challenge && <p className="my-2 text-sm">{challenge.open_date} → {challenge.close_date} · {challenge.status === "results_published" ? "Published results" : "CMS preview — results not published"}</p>}
    {error && <p role="alert">{error}</p>}<div className="divide-y mt-3">{rankings.map(row => <div key={row.user_id} className="flex justify-between py-3"><div><strong>#{row.rank} {row.user_name}</strong><p className="text-sm">{(row.total_completion_ms / 1000).toFixed(1)}s completion{row.rank === 1 ? " · Winner" : ""}</p></div><span>{row.total_score} points</span></div>)}</div>
  </section>;
}

const SUBTABS = [
  { key: "categories", label: "Categories", icon: <Layers className="w-4 h-4" /> },
  { key: "practice", label: "Practice Bank", icon: <BookOpen className="w-4 h-4" /> },
  { key: "challenge", label: "Challenge Bank", icon: <Shield className="w-4 h-4" /> },
  { key: "weekly", label: "Weekly Challenges", icon: <Swords className="w-4 h-4" /> },
  { key: "csv", label: "CSV Import", icon: <Upload className="w-4 h-4" /> },
  { key: "rankings", label: "Weekly Rankings", icon: <Trophy className="w-4 h-4" /> },
  { key: "monthly", label: "Monthly Winners", icon: <Trophy className="w-4 h-4" /> },
  { key: "champions", label: "Annual Championship", icon: <Trophy className="w-4 h-4" /> },
  { key: "settings", label: "Settings", icon: <SettingsIcon className="w-4 h-4" /> },
];

export function QuizTab({ role }) {
  const [sub, setSub] = useState("categories");
  const isSuper = role === "super_admin";
  return (
    <div className="space-y-6" data-testid="quiz-tab">
      <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center"><ListChecks className="w-5 h-5 text-[#2C4C3B]" /></div>
          <div>
            <div className="font-serif-display text-xl text-[#1C201E]">Bible Quiz Manager</div>
            <div className="text-xs text-[#5C6661]">Practice Bank (unlimited) &amp; Weekly Challenge Bank (competitive). Snapshots and scores are immutable; auto-transfer runs on Sunday publication.</div>
          </div>
        </div>
        <div className="inline-flex bg-[#F3EFEA] border border-[#E5E0D8] rounded-full p-1 flex-wrap">
          {SUBTABS.map((s) => (
            <button key={s.key} data-testid={`quiz-subtab-${s.key}`} onClick={() => setSub(s.key)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${sub === s.key ? "bg-[#2C4C3B] text-white" : "text-[#1C201E]"}`}>{s.icon}{s.label}</button>
          ))}
        </div>
      </div>

      {sub === "categories" && <CategoriesPanel />}
      {sub === "practice" && <QuestionBankPanel pool="practice" />}
      {sub === "challenge" && <QuestionBankPanel pool="challenge" />}
      {sub === "weekly" && <WeeklyChallengesPanel />}
      {sub === "csv" && <CsvImportPanel />}
      {sub === "rankings" && <RankingsPanel/>}
      {sub === "monthly" && <RankingsPanel period="monthly"/>}
      {sub === "champions" && <ChampionsPanel />}
      {sub === "settings" && <SettingsPanel isSuper={isSuper} />}
    </div>
  );
}

export default QuizTab;
